import {cva} from 'class-variance-authority'

/**
 * IconButton (v3, MUI-aligned).
 * `color` (primary/secondary/error) × `appearance` (default/outline/filled) × `size`.
 *
 * - `filled` — solid color fill, white icon.
 * - `outline` — 1px colored border, colored icon, transparent.
 * - `default` — colored icon, transparent, tinted hover (plain icon button).
 *
 * Shape is size-dependent per Figma: `small` (32px) and `medium` (40px) are
 * circular pills; `large` (48px) is a rounded square (`rounded-2xl` = 16px).
 */
export const IconButtonVariantsFactory = cva(
  'inline-flex shrink-0 select-none items-center justify-center transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      appearance: {
        default: '',
        outline: 'border',
        filled: '',
      },
      color: {
        primary: '',
        secondary: '',
        error: '',
      },
      size: {
        small: 'h-8 w-8 rounded-full',
        medium: 'h-10 w-10 rounded-full',
        large: 'h-12 w-12 rounded-2xl',
      },
    },
    compoundVariants: [
      // filled
      {
        appearance: 'filled',
        color: 'primary',
        class:
          'bg-primary text-white hover:bg-primary-dark disabled:bg-action-disabled-background disabled:text-action-disabled',
      },
      {
        appearance: 'filled',
        color: 'secondary',
        class:
          'bg-secondary text-white hover:bg-secondary-dark disabled:bg-action-disabled-background disabled:text-action-disabled',
      },
      {
        appearance: 'filled',
        color: 'error',
        class:
          'bg-error text-white hover:bg-error-dark disabled:bg-action-disabled-background disabled:text-action-disabled',
      },
      // outline
      {
        appearance: 'outline',
        color: 'primary',
        class:
          'border-[#00a2b1] text-primary hover:bg-primary-faint disabled:border-action-disabled-background disabled:text-action-disabled',
      },
      {
        appearance: 'outline',
        color: 'secondary',
        class:
          'border-[#2c686f] text-secondary hover:bg-secondary-faint disabled:border-action-disabled-background disabled:text-action-disabled',
      },
      {
        appearance: 'outline',
        color: 'error',
        class:
          'border-error text-error hover:bg-error-faint disabled:border-action-disabled-background disabled:text-action-disabled',
      },
      // default
      {
        appearance: 'default',
        color: 'primary',
        class: 'text-primary hover:bg-primary-faint disabled:text-action-disabled',
      },
      {
        appearance: 'default',
        color: 'secondary',
        class: 'text-secondary hover:bg-secondary-faint disabled:text-action-disabled',
      },
      {
        appearance: 'default',
        color: 'error',
        class: 'text-error hover:bg-error-faint disabled:text-action-disabled',
      },
    ],
    defaultVariants: {
      appearance: 'filled',
      color: 'primary',
      size: 'medium',
    },
  },
)
